import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wallet, Phone, FileText, Upload, Trash2, Loader2,
  Lock, Unlock, ExternalLink, Bell, BellOff, ChevronDown, ChevronUp,
  Archive, ArchiveRestore, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  ADMIN_TOKEN_KEY, adminClients, adminWallet, adminCalls, adminCallDetail,
  adminKnowledgeList, adminKnowledgeUpload, adminKnowledgeDelete,
  adminClientSettings, adminUpdateClient, adminRecharge, creditColor, eur,
  adminArchiveClient, adminDeleteClient,
} from "../lib/adminApi";

const DOT = { green: "#3A4D39", yellow: "#D9A441", red: "#C0392B" };

export default function PlanciaCliente() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);

  const [client, setClient] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [calls, setCalls] = useState([]);
  const [docs, setDocs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);

  const handle401 = (err) => {
    if (err?.response?.status === 401) { toast.error("Sessione scaduta"); navigate("/plancia"); return true; }
    return false;
  };

  const load = useCallback(async () => {
    if (!token) { navigate("/plancia"); return; }
    setLoading(true);
    try {
      const [cl, w, cs, kn, st] = await Promise.all([
        adminClients(token), adminWallet(token, slug), adminCalls(token, slug),
        adminKnowledgeList(token, slug), adminClientSettings(token, slug),
      ]);
      setClient((cl.clients || []).find((c) => c.slug === slug) || { slug });
      setWallet(w); setCalls(cs.calls || []); setDocs(kn.docs || []); setSettings(st);
    } catch (err) { if (!handle401(err)) toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, [token, slug]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggleSospeso = async () => {
    try {
      await adminUpdateClient(token, slug, { sospeso: !client?.sospeso });
      toast.success(!client?.sospeso ? "Cliente bloccato" : "Cliente sbloccato");
      load();
    } catch (err) { if (!handle401(err)) toast.error("Operazione fallita"); }
  };

  const toggleTelegram = async () => {
    try {
      await adminUpdateClient(token, slug, { telegram_enabled: !settings?.telegram_enabled });
      toast.success("Telegram aggiornato");
      load();
    } catch (err) { if (!handle401(err)) toast.error("Operazione fallita"); }
  };

  const toggleArchivia = async () => {
    const toArchive = !client?.archiviato;
    if (toArchive && !window.confirm(
      `Archiviare "${client?.company_name || slug}"? Smette di ricevere chiamate ed esce dalla lista, ma TUTTI i dati restano. Puoi ripristinarlo quando vuoi.`
    )) return;
    try {
      await adminArchiveClient(token, slug, toArchive);
      if (toArchive) { toast.success("Cliente archiviato"); navigate("/plancia"); }
      else { toast.success("Cliente ripristinato"); load(); }
    } catch (err) { if (!handle401(err)) toast.error("Operazione fallita"); }
  };

  if (loading && !client) {
    return <div className="min-h-screen bg-brand-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-terra" /></div>;
  }

  const voce = (client?.default_gender || "F").toUpperCase() === "M" ? "Alberto (uomo)" : "Anna (donna)";

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/plancia")} className="flex items-center gap-2 text-sm text-brand-inkSoft hover:text-brand-ink mb-4">
          <ArrowLeft className="w-4 h-4" /> Plancia
        </button>

        {/* Intestazione */}
        <div className="bg-white border border-brand-line rounded-2xl p-5 mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl text-brand-ink">{client?.company_name || slug}</h1>
            <p className="text-sm text-brand-inkSoft">{client?.numero || "nessun numero assegnato"} · voce {voce}</p>
            {client?.sospeso && <span className="inline-block mt-2 mr-2 text-xs px-2 py-1 rounded-full bg-[#FBEAEA] text-[#C0392B] font-medium">bloccato</span>}
            {client?.archiviato && <span className="inline-block mt-2 text-xs px-2 py-1 rounded-full bg-[#EDE7DD] text-brand-inkSoft font-medium">archiviato</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={toggleSospeso} className="px-3 py-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2 text-sm font-medium flex items-center gap-2">
              {client?.sospeso ? <><Unlock className="w-4 h-4" /> Sblocca</> : <><Lock className="w-4 h-4" /> Blocca</>}
            </button>
            <button onClick={toggleTelegram} className="px-3 py-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2 text-sm font-medium flex items-center gap-2">
              {settings?.telegram_enabled ? <><BellOff className="w-4 h-4" /> Telegram off</> : <><Bell className="w-4 h-4" /> Telegram on</>}
            </button>
            <a href={`/dashboard/${slug}`} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2 text-sm font-medium flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Dashboard cliente
            </a>
            <button onClick={toggleArchivia} className="px-3 py-2 rounded-lg border border-brand-line bg-white hover:bg-brand-bg2 text-sm font-medium flex items-center gap-2">
              {client?.archiviato ? <><ArchiveRestore className="w-4 h-4" /> Ripristina</> : <><Archive className="w-4 h-4" /> Archivia</>}
            </button>
            <button onClick={() => setShowDelete(true)} className="px-3 py-2 rounded-lg border border-[#E3B7B0] bg-white hover:bg-[#FBEAEA] text-[#C0392B] text-sm font-medium flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Elimina
            </button>
          </div>
        </div>

        {/* Borsellino */}
        <WalletBox token={token} slug={slug} wallet={wallet} onReload={load} on401={handle401} />

        {/* Chiamate */}
        <CallsBox token={token} slug={slug} calls={calls} on401={handle401} />

        {/* Knowledge */}
        <KnowledgeBox token={token} slug={slug} docs={docs} onReload={load} on401={handle401} />
      </div>

      {showDelete && (
        <DeleteClientModal
          companyName={client?.company_name || slug}
          onClose={() => setShowDelete(false)}
          onConfirm={async (conferma) => {
            try {
              await adminDeleteClient(token, slug, conferma);
              toast.success("Cliente eliminato definitivamente");
              navigate("/plancia");
            } catch (err) {
              if (!handle401(err)) toast.error(err?.response?.data?.detail || "Eliminazione fallita");
            }
          }}
        />
      )}
    </div>
  );
}

function DeleteClientModal({ companyName, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const match = text.trim() === String(companyName || "").trim();
  const go = async () => {
    if (!match || busy) return;
    setBusy(true);
    await onConfirm(text.trim());
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-brand-bg border border-brand-line rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-line">
          <h3 className="font-heading text-xl text-[#C0392B] flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Elimina definitivamente</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-brand-bg2"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-brand-ink">
            Questa azione è <b>irreversibile</b>. Cancella il cliente e <b>tutti i suoi dati</b>:
            chiamate, movimenti del borsellino, knowledge base e numero assegnato.
          </p>
          <p className="text-sm text-brand-inkSoft">
            Se vuoi solo sospenderlo conservando lo storico, usa invece <b>Archivia</b>.
          </p>
          <div>
            <label className="text-xs text-brand-inkSoft block mb-1">
              Per confermare, scrivi esattamente: <b className="text-brand-ink">{companyName}</b>
            </label>
            <input value={text} onChange={(e) => setText(e.target.value)} autoFocus
              className="input-clean w-full px-3 py-2 rounded-lg text-sm" placeholder={companyName} />
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-lg border border-brand-line bg-white font-semibold">Annulla</button>
            <button onClick={go} disabled={!match || busy}
              className="flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-white disabled:opacity-40"
              style={{ background: "#C0392B" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Elimina per sempre
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WalletBox({ token, slug, wallet, onReload, on401 }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const bal = wallet?.balance_eur ?? 0;
  const col = creditColor(bal);
  const recharge = async () => {
    const a = parseFloat(String(amount).replace(",", "."));
    if (isNaN(a) || a === 0) { toast.error("Importo non valido"); return; }
    setBusy(true);
    try {
      const res = await adminRecharge(token, slug, a, "Ricarica da plancia (dettaglio)");
      toast.success(`Nuovo saldo: ${eur(res.balance_eur)}`); setAmount(""); onReload();
    } catch (err) { if (!on401(err)) toast.error("Ricarica fallita"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-2 text-brand-inkSoft mb-3"><Wallet className="w-5 h-5" /><span className="text-xs uppercase tracking-wider">Borsellino</span></div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="font-heading text-4xl" style={{ color: DOT[col] }}>{eur(bal)}</div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-brand-inkSoft block mb-1">Importo (€, negativo = rettifica)</label>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="es. 20"
              className="input-clean px-3 py-2 rounded-lg text-sm w-36" />
          </div>
          <button onClick={recharge} disabled={busy} className="btn-olive px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ricarica"}
          </button>
        </div>
      </div>
      <div className="border-t border-brand-line pt-3">
        <p className="text-xs uppercase tracking-wider text-brand-inkSoft mb-2">Movimenti</p>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {(wallet?.movimenti || []).length === 0 && <p className="text-sm text-brand-inkSoft">Nessun movimento.</p>}
          {(wallet?.movimenti || []).map((m, i) => (
            <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-brand-line/50 last:border-0">
              <div className="min-w-0">
                <span className="font-medium">{m.descrizione || m.tipo}</span>
                <span className="text-xs text-brand-inkSoft ml-2">{(m.ts || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <span className={`font-mono ${(m.amount_eur || 0) >= 0 ? "text-brand-olive" : "text-[#C0392B]"}`}>
                {(m.amount_eur || 0) >= 0 ? "+" : ""}{eur(m.amount_eur)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CallsBox({ token, slug, calls, on401 }) {
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({});
  const toggle = async (callId) => {
    if (openId === callId) { setOpenId(null); return; }
    setOpenId(callId);
    if (!detail[callId]) {
      try {
        const d = await adminCallDetail(token, slug, callId);
        setDetail((s) => ({ ...s, [callId]: d }));
      } catch (err) { if (!on401(err)) toast.error("Errore dettaglio chiamata"); }
    }
  };
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-2 text-brand-inkSoft mb-3"><Phone className="w-5 h-5" /><span className="text-xs uppercase tracking-wider">Chiamate</span></div>
      {calls.length === 0 && <p className="text-sm text-brand-inkSoft">Nessuna chiamata registrata.</p>}
      <div className="space-y-2">
        {calls.map((c) => {
          const s = c.summary || {};
          const open = openId === c.call_id;
          const d = detail[c.call_id];
          return (
            <div key={c.call_id} className="border border-brand-line rounded-lg overflow-hidden">
              <button onClick={() => toggle(c.call_id)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-brand-bg2 text-left">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.nome_cliente || "Cliente"} · <span className="text-brand-inkSoft">{s.motivo_chiamata || s.riassunto_breve || "—"}</span></div>
                  <div className="text-xs text-brand-inkSoft">{(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
              </button>
              {open && (
                <div className="px-3 py-3 bg-brand-bg2 text-sm space-y-2">
                  {s.riassunto_breve && <p><b>Riassunto:</b> {s.riassunto_breve}</p>}
                  {s.contatti_lasciati && <p><b>Contatti:</b> {s.contatti_lasciati}</p>}
                  {s.azione_richiesta && <p><b>Da fare:</b> {s.azione_richiesta}</p>}
                  {!d && <p className="text-brand-inkSoft flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> carico trascrizione…</p>}
                  {d?.messages?.length > 0 && (
                    <div className="border-t border-brand-line pt-2 mt-2 space-y-1 max-h-60 overflow-y-auto">
                      {d.messages.map((m, i) => (
                        <div key={i} className="text-xs">
                          <span className={m.role === "user" ? "text-brand-terra font-semibold" : "text-brand-olive font-semibold"}>
                            {m.role === "user" ? "Cliente" : "AI"}:
                          </span> {m.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KnowledgeBox({ token, slug, docs, onReload, on401 }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await adminKnowledgeUpload(token, slug, file); toast.success("Documento caricato"); onReload(); }
    catch (err) { if (!on401(err)) toast.error(err?.response?.data?.detail || "Upload fallito"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (docId) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try { await adminKnowledgeDelete(token, slug, docId); toast.success("Eliminato"); onReload(); }
    catch (err) { if (!on401(err)) toast.error("Eliminazione fallita"); }
  };
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-5 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-brand-inkSoft"><FileText className="w-5 h-5" /><span className="text-xs uppercase tracking-wider">Knowledge base</span></div>
        <label className="btn-olive px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 cursor-pointer">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica
          <input type="file" accept=".pdf,.txt,.md" onChange={upload} className="hidden" disabled={busy} />
        </label>
      </div>
      {docs.length === 0 && <p className="text-sm text-brand-inkSoft">Nessun documento caricato.</p>}
      <div className="space-y-1">
        {docs.map((d) => (
          <div key={d.doc_id} className="flex items-center justify-between text-sm py-2 border-b border-brand-line/50 last:border-0">
            <div className="min-w-0"><span className="font-medium truncate">{d.filename}</span>
              <span className="text-xs text-brand-inkSoft ml-2">{d.char_count} caratteri</span></div>
            <button onClick={() => del(d.doc_id)} className="p-2 rounded-lg border border-brand-line hover:bg-brand-bg2 text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
